import React, { ChangeEvent, FC, FormEvent, useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from '../translations';
import { captureError } from '../utils/errorHandling';
import { MaterialIcon } from '.';

interface AssetUploadProps {
  readonly category: 'badge-art' | 'product';
  readonly onComplete: () => void;
  readonly onFileUploaded: (assetId: string) => Promise<void>;
}

const stopEvent = (e: React.DragEvent<HTMLInputElement>) => {
  e.preventDefault();
  e.stopPropagation();
};

export const AssetUpload: FC<AssetUploadProps> = ({ category, onComplete, onFileUploaded }) => {
  const { ts } = useTranslation();

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const uploadFiles = useCallback(
    async (files: FileList) => {
      setIsUploading(true);

      try {
        await Promise.all(
          Array.from(files).map(async (file: Blob) => {
            const { assetId } = await api.createAsset(category, file);
            await onFileUploaded(assetId);
          }),
        );

        toast.success(ts('asset_upload.upload_complete'));
      } catch (error) {
        captureError(error as Error);
      }

      onComplete();
      setIsUploading(false);
    },
    [onComplete, ts, category, onFileUploaded],
  );

  const onChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        await uploadFiles(e.target.files);
      }
    },
    [uploadFiles],
  );

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLInputElement>) => {
      await uploadFiles(e.dataTransfer.files);
    },
    [uploadFiles],
  );

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const uploadElem = uploadInputRef.current;
      if (uploadElem?.files) {
        await uploadFiles(uploadElem.files);
      }
    },
    [uploadFiles],
  );

  const openFileUpload = useCallback(() => {
    if (uploadInputRef.current && !isUploading) {
      uploadInputRef.current.click();
    }
  }, [uploadInputRef, isUploading]);

  return (
    <form id="fileUploadForm" onSubmit={onSubmit}>
      <input
        accept="image/png"
        className="d-none"
        disabled={isUploading}
        id="fileUpload"
        multiple
        name="files[]"
        onChange={onChange}
        onDrag={stopEvent}
        onDragEnd={stopEvent}
        onDragEnter={stopEvent}
        onDragLeave={stopEvent}
        onDragOver={stopEvent}
        onDragStart={stopEvent}
        onDrop={onDrop}
        ref={uploadInputRef}
        type="file"
      />
      <div
        className="cardbtn"
        onClick={openFileUpload}
        style={{ height: '200px', textAlign: 'center', paddingTop: '4em' }}
      >
        <MaterialIcon name="cloud_upload" style={{ fontSize: '24px' }} />
        <h4>{ts(isUploading ? 'common.uploading' : 'asset_upload.upload_files')}</h4>
        <small>{ts('asset_upload.parenthesis_only_png')}</small>
      </div>
    </form>
  );
};
